import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import dayjs from 'dayjs';
import { Select } from 'antd';
import { formatDate, formatLocaleNumber, formatMoneyCompact, formatMoneyWithSeparators } from '@/utils';
import { DateFormats } from '@/constants/dates';
import { COLORS } from '@/theme';
import { IDualSeriesItem, IStockSeriesItem, MainMetricsChartMode, MainMetricsGroupBy } from '@/models/dashboard.model';
import {
  MAIN_METRICS_CHART_MARGIN,
  MAIN_METRICS_Y_AXIS_WIDTH,
  StyledChartBox,
  StyledPanel,
  StyledPanelTitle,
  StyledToggleGroup,
  StyledToolbar,
} from '../MainMetricsSharedStyles';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tooltip as UiTooltip } from '@/ui';

type ChartSeriesItem = IDualSeriesItem | IStockSeriesItem;

interface IDualLineChartProps {
  title: string;
  tooltip?: string;
  series: ChartSeriesItem[];
  initialGroupBy?: MainMetricsGroupBy;
  showModeToggle?: boolean;
}

const aggregateSeries = (series: ChartSeriesItem[], groupBy: MainMetricsGroupBy): ChartSeriesItem[] => {
  if (groupBy === 'day') return series;
  const buckets = new Map<string, ChartSeriesItem>();
  series.forEach((item) => {
    const key = dayjs(item.date).startOf('week').format('YYYY-MM-DD');
    const existing = buckets.get(key);
    if (!existing) {
      buckets.set(key, { ...item, date: key });
      return;
    }
    const existingCount = 'count' in existing ? existing.count ?? 0 : 0;
    const itemCount = 'count' in item ? item.count ?? 0 : 0;
    const existingPrevCount = 'prev_count' in existing ? existing.prev_count ?? 0 : 0;
    const itemPrevCount = 'prev_count' in item ? item.prev_count ?? 0 : 0;
    buckets.set(key, {
      date: key,
      amount: existing.amount + item.amount,
      count: existingCount + itemCount,
      prev_amount: (existing.prev_amount ?? 0) + (item.prev_amount ?? 0),
      prev_count: existingPrevCount + itemPrevCount,
    });
  });
  return Array.from(buckets.values());
};

const DualLineChart = ({
  title,
  tooltip,
  series,
  initialGroupBy = 'day',
  showModeToggle = true,
}: IDualLineChartProps) => {
  const { t, i18n } = useTranslation(['dashboard']);
  const [mode, setMode] = useState<MainMetricsChartMode>('amount');
  const [groupBy, setGroupBy] = useState<MainMetricsGroupBy>(initialGroupBy);

  useEffect(() => {
    setGroupBy(initialGroupBy);
  }, [initialGroupBy]);

  const chartData = useMemo(() => aggregateSeries(series, groupBy), [series, groupBy]);
  const currentKey = mode === 'amount' ? 'amount' : 'count';
  const prevKey = mode === 'amount' ? 'prev_amount' : 'prev_count';

  const formatAxisTick = (value: number) =>
    mode === 'amount' ? formatMoneyCompact(value, i18n.language) : formatLocaleNumber(value, 0, i18n.language);

  return (
    <StyledPanel>
      <StyledToolbar>
        <StyledPanelTitle>
          {title}
          {tooltip && (
            <UiTooltip title={tooltip}>
              <InfoCircleOutlined
                style={{
                  marginLeft: 6,
                  color: COLORS.GRAY_PRIMARY,
                  cursor: 'help',
                }}
              />
            </UiTooltip>
          )}
        </StyledPanelTitle>

        <StyledToggleGroup>
          {showModeToggle ? (
            <Select
              value={mode}
              style={{ minWidth: 140 }}
              onChange={(value) => setMode(value as MainMetricsChartMode)}
              options={[
                { value: 'amount', label: t('main_metrics_mode_amount') },
                { value: 'count', label: t('main_metrics_mode_count') },
              ]}
            />
          ) : null}
          <Select
            value={groupBy}
            style={{ minWidth: 140 }}
            onChange={(value) => setGroupBy(value as MainMetricsGroupBy)}
            options={[
              { value: 'day', label: t('main_metrics_group_day') },
              { value: 'week', label: t('main_metrics_group_week') },
            ]}
          />
        </StyledToggleGroup>
      </StyledToolbar>
      <StyledChartBox>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={MAIN_METRICS_CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.BRIGHT_GRAY} />
            <XAxis
              dataKey="date"
              tickFormatter={(value: string) => formatDate(value, DateFormats.DATE_WITHOUT_HOURS)}
              minTickGap={24}
            />
            <YAxis tickFormatter={formatAxisTick} width={MAIN_METRICS_Y_AXIS_WIDTH} />
            <Tooltip
              formatter={(value: number) =>
                mode === 'amount'
                  ? `${formatMoneyWithSeparators(value)} ${t('dashboard_grn')}`
                  : formatLocaleNumber(value, 0, i18n.language)
              }
              labelFormatter={(label: string) => formatDate(label, DateFormats.DATE_WITHOUT_HOURS)}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey={currentKey}
              name={t('main_metrics_current_period')}
              stroke={COLORS.GREEN}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey={prevKey}
              name={t('main_metrics_previous_period')}
              stroke={COLORS.RED}
              strokeDasharray="5 5"
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </StyledChartBox>
    </StyledPanel>
  );
};

export default DualLineChart;
